'use client';

import React, { useRef, createContext, useContext } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { twMerge } from 'tailwind-merge';

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** `full` covers the viewport edge to edge (e.g. a presentation). */
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  hideBackdrop?: boolean;
  showCloseButton?: boolean;
  className?: string;
  disableClickOutside?: boolean;
  topAligned?: boolean;
  slotProps?: {
    backDropClassName?: string;
    containerClassName?: string;
  };
  /** ARIA labelledby attribute for accessibility */
  'aria-labelledby'?: string;
  /** ARIA describedby attribute for accessibility */
  'aria-describedby'?: string;
  /** Data test ID for testing */
  'data-testid'?: string;
}

// Create context to share onClose function
const DialogContext = createContext<{ onClose: () => void } | undefined>(
  undefined
);

const Dialog = ({
  isOpen,
  onClose,
  children,
  size = 'md',
  hideBackdrop = false,
  showCloseButton = false,
  className = '',
  disableClickOutside = false,
  topAligned = false,
  slotProps,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  'data-testid': dataTestId,
}: DialogProps) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'w-full max-w-sm h-48 m-4 rounded-lg', // 192px
    md: 'w-full max-w-md h-64 m-4 rounded-lg', // 256px
    lg: 'w-full max-w-lg h-80 m-4 rounded-lg', // 320px
    xl: 'w-full max-w-xl h-96 m-4 rounded-lg', // 384px
    full: 'w-full h-full',
  };

  return createPortal(
    <DialogContext.Provider value={{ onClose }}>
      <div
        data-dialog-portal="true"
        className={twMerge(
          'fixed inset-0 z-[9999] flex justify-center pointer-events-none',
          topAligned ? 'items-start' : 'items-center',
          slotProps?.containerClassName
        )}
      >
        {!hideBackdrop && (
          <div
            className={twMerge(
              'absolute inset-0 bg-black/50 backdrop-blur-sm p-2 pointer-events-auto',
              slotProps?.backDropClassName
            )}
            onClick={!disableClickOutside ? onClose : undefined}
          />
        )}
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          data-testid={dataTestId}
          className={twMerge(
            'relative w-full bg-white shadow-xl flex flex-col overflow-hidden scrollbar-gutter-stable pointer-events-auto',
            sizeClasses[size],
            className
          )}
        >
          {showCloseButton && <Dialog.CloseButton />}
          {children}
        </div>
      </div>
    </DialogContext.Provider>,
    document.body
  );
};

const DialogCloseButton = ({ className = '' }: { className?: string }) => {
  const context = useContext(DialogContext);
  if (!context) throw new Error('CloseButton must be used within a Dialog');
  return (
    <button
      onClick={context.onClose}
      className={`absolute top-4 right-4 w-5 h-5 flex items-center font-bold
        justify-center text-neutral-500 hover:text-neutral-600 transition-colors ${className}`}
      aria-label="Close dialog"
    >
      <Icon icon={faTimes} className="w-4 h-4" />
    </button>
  );
};
DialogCloseButton.displayName = 'Dialog.CloseButton';
Dialog.CloseButton = DialogCloseButton;

const DialogHeader = ({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`p-4 border-b border-neutral-200 flex items-center ${className}`}
  >
    <div className="flex-1">{children}</div>
  </div>
);
DialogHeader.displayName = 'Dialog.Header';
Dialog.Header = DialogHeader;

const DialogContent = ({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={`flex-1 overflow-y-auto min-h-0 ${className}`}>
    {children}
  </div>
);
DialogContent.displayName = 'Dialog.Content';
Dialog.Content = DialogContent;

const DialogActions = ({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={`px-6 py-4 border-t border-neutral-200 flex items-center justify-end gap-2 ${className}`}
  >
    {children}
  </div>
);
DialogActions.displayName = 'Dialog.Actions';
Dialog.Actions = DialogActions;

export default Dialog;
